import {DeleteOutlined, EditOutlined} from '@ant-design/icons';
import {useUser} from '@dofleini/security';
import {partner} from '@mep/pae-commons';
import {Button, Checkbox, Divider, Image, message} from 'antd';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, {memo, useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useMutation, useQueryClient} from 'react-query';
import {Link} from 'react-router-dom';

import {ReactComponent as MainPartner} from '@/assets/images/main-partner.svg';
import BooleanPreview from '@/components/BooleanPreview';
import CountryPreview from '@/components/CountryPreview';
import GenderPreview from '@/components/GenderPreview';
import InfoBox from '@/components/InfoBox';
import Phones from '@/components/PhoneNumberField/PhonesListView';
import SkinColorPreview from '@/components/SkinColorPreview';
import StudyClassificationPreview from '@/components/StudyClassificationPreview';
import StudyLevelPreview from '@/components/StudyLevelPreview';
import useAlerts from '@/hooks/useAlerts';
import {showConfirm} from '@/hooks/useCrud';
import {usePermissions} from '@/hooks/usePermissions';
import {useIsAdminByRoles} from '@/modules/admin/hooks/useUser';
import {CACHE_KEY_COMPANY, CACHE_KEY_LIST_COMPANY} from '@/modules/company/contants/company';
import CompanyApiService from '@/modules/company/services/CompanyApiService';
import PartnerStatus from '@/modules/proposalRequest/components/PartnerStatus';
import CheckSymbolPartner
  from '@/modules/proposalRequest/components/ProposalCreateForm/ProposalVerifyData/components/PartnerProposal/components/CheckSymbolPartner/CheckSymbolPartner';
import ContributionGoods
  from '@/modules/proposalRequest/components/ProposalCreateForm/ProposalVerifyData/components/PartnerProposal/components/ContributionGoods';
import ContributionInput
  from '@/modules/proposalRequest/components/ProposalCreateForm/ProposalVerifyData/components/PartnerProposal/components/ContributionInput';
import MarriedInformationDetails
  from '@/modules/proposalRequest/components/ProposalCreateForm/ProposalVerifyData/components/PartnerProposal/components/MarriedInformationDetails';
import {
  useProposalsByPartner
} from '@/modules/proposalRequest/components/ProposalCreateForm/ProposalVerifyData/components/PartnerProposal/hooks';
import VerifyListItem
  from '@/modules/proposalRequest/components/ProposalCreateForm/ProposalVerifyData/components/VerifyListItem';
import ProposalPartnerImportSelect
  from '@/modules/proposalRequest/components/ProposalPartnerImportSelect/ProposalPartnerImportSelect';
import style from '@/modules/proposalRequest/components/ProposalPrivatePartners/index.less';
import ProposalSimilarItem
  from '@/modules/proposalRequest/containers/ProposalRequestDetailContainer/layout/ProposalHeader/component/ProposalSimilarItem';
import {
  CACHE_KEY_LIST_PROPOSAL_REQUEST,
  CACHE_KEY_PROPOSAL_REQUEST,
  ROUTE_PATH_PROPOSAL_REQUEST,
  SECURITY_KEY_PROPOSAL_REQUEST
} from '@/modules/proposalRequest/contants/proposalRequest';
import {useAddressFull} from '@/modules/proposalRequest/hooks/useAddressFull';
import {usePartnersCapital} from '@/modules/proposalRequest/hooks/usePartnersCapital';
import ProposalRequestApiService from '@/modules/proposalRequest/services/ProposalRequestApiService';
import {getFromSource} from '@/utils/getImages';
import {getUserFullName} from '@/utils/userFullName';
import {showPrice} from '@/utils/utils';

const {CIVIL_STATUS, PARTNERSHIP_REQUEST} = partner;

const checkFucData = (fucData = {}) => {
  return Object.values(fucData).some((value) => (value === false));
};

const PartnerProposal = ({
  data,
  extraTitle,
  onEditPartner,
  onDeletePartner,
  onlyRead,
  onChange,
  isStatePartnerForm,
  disableContribution,
  isLast,
  index,
  showStatusActions,
  proposalId,
  companyId,
  isCNA,
  proposal,
  cashEdit = true,
  hideVerifyIcon = false,
  isCompanyView = false,
  allowChangeRepresentative = false,
  isProcedure = false,
  isTCPProposal = false,
  allowFullChanges,
  ...props
}) => {
  const {canWrite} = usePermissions(SECURITY_KEY_PROPOSAL_REQUEST);
  const {user, hasPermission} = useUser();
  const queryClient = useQueryClient();
  const {t} = useTranslation('proposalRequest');
  const {putErrorCode} = useAlerts();
  const isAdmin = useIsAdminByRoles();
  const [element, setElement] = useState({importBase: 2500, ...data} || {importBase: 2500});
  
  const {
    goods, contribution, address, email, phone, representative, idNumber, civilStatus, dniPictures, gender,
    bornPlace, workPlace, isMatrimonialCommunity, importBase, civilStatusInfo, skinColor, studyLevel,
    studyClassification
  } = data || {};
  
  const {exist, proposals} = useProposalsByPartner(proposalId, idNumber, {
    enabled: (!!proposalId && !!idNumber) && !isCompanyView
  });
  
  const mainAddress = useAddressFull(address);
  
  const {totalAmount} = usePartnersCapital([{contribution, goods}]);
  
  const {status} = element || {};
  
  const onChangeContribution = useCallback(({value, isMatrimonialCommunity}) => {
    data.contribution = value;
    data.isMatrimonialCommunity = isMatrimonialCommunity;
    onChange && onChange(data, index);
  }, [data, index, onChange]);
  
  const onChangeImportBase = useCallback((value) => {
    data.importBase = value;
    onChange && onChange(data, index);
  }, [data, index, onChange]);
  
  const onChangeIsRetired = useCallback((value) => {
    data.isRetired = value;
    data.importBase = null;
    onChange && onChange(data, index);
  }, [data, index, onChange]);
  
  const onChangeGoods = useCallback((goods) => {
    data.goods = goods;
    onChange && onChange(data, index);
  }, [data, index, onChange]);
  
  const onFinishUpdate = useCallback((partner = null) => {
    message.success(t('updateSuccess'));
    queryClient.invalidateQueries(CACHE_KEY_LIST_PROPOSAL_REQUEST).then();
    queryClient.invalidateQueries(CACHE_KEY_PROPOSAL_REQUEST).then();
    //
    queryClient.invalidateQueries(CACHE_KEY_LIST_COMPANY).then();
    queryClient.invalidateQueries(CACHE_KEY_COMPANY).then();
    //
    queryClient.invalidateQueries(proposalId).then();
    queryClient.invalidateQueries([proposalId, CACHE_KEY_PROPOSAL_REQUEST]).then();
    if (partner)
      setElement({...data, status: partner?.status});
  }, [data, proposalId, queryClient, t]);
  
  const {mutateAsync: verifyAsync, isLoading: checking} = useMutation(ProposalRequestApiService.partnerVerify, {
    onSuccess: ({data: response}) => {
      onFinishUpdate(response?.partners?.find(e => e?.idNumber === idNumber) || null);
    },
    onError: (e) => {
      putErrorCode(e);
    }
  });
  
  const {mutateAsync: rejectAsync, isLoading: rejecting} = useMutation(ProposalRequestApiService.partnerReject, {
    onSuccess: ({data: response}) => {
      onFinishUpdate(response?.partners?.find(e => e?.idNumber === idNumber) || null);
    },
    onError: (e) => {
      putErrorCode(e);
    }
  });
  
  const {mutateAsync: updateRepresentative} = useMutation((isCompanyView ? CompanyApiService : ProposalRequestApiService).updateRepresentative, {
    onSuccess: () => {
      onFinishUpdate();
    },
    onError: (e) => {
      putErrorCode(e);
    }
  });
  
  const fucVerified = useMemo(() => {
    if (data) {
      if (!data?.fucVerified) {
        return <CheckSymbolPartner hideCheck={false} isValid={false} proposalId={proposalId}/>;
      }
      if (checkFucData(data?.fucVerification)) {
        return <CheckSymbolPartner hideCheck={false} isValid={false} proposalId={proposalId}/>;
      }
      return <CheckSymbolPartner hideCheck={false} isValid={true} proposalId={proposalId}/>;
    }
  }, [data, proposalId]);
  
  const handleRepresentativeChange = useCallback(async () => {
    await updateRepresentative({id: companyId || proposalId, idNumber, partners: proposal?.partners});
  }, [companyId, idNumber, proposal?.partners, proposalId, updateRepresentative]);
  
  const actionCmp = useMemo(() => {
    return <div className="md:ml-2 flex-1 flex items-center justify-between">
      {!isTCPProposal ? <>
        {representative ? <div className="flex items-center text-primary text-xs">
          <MainPartner/>
          <div className="ml-1">{t('mainPartner')}</div>
        </div> : ((allowChangeRepresentative && canWrite &&
          hasPermission(
            isCompanyView ?
              'COMPANY:CHANGE_REPRESENTATIVE_PARTNER' :
              'REQUEST:CHANGE_REPRESENTATIVE_PARTNER'
          )
        ) ?
          <div
            className='text-warning text-xs cursor-pointer'
            onClick={() => showConfirm(t, getUserFullName(data), handleRepresentativeChange, 'proposalDetails.changeRepresentative.question')}
          >
            {t('proposalDetails.changeRepresentative.label')}
          </div> : <div/>)}
      </> : <div/>}
      {(allowFullChanges || (!isCompanyView && (!onlyRead && canWrite))) &&
        <div className="flex items-center justify-between">
          <div className={classNames(style.actionButtonContainer, 'cursor-pointer')} onClick={onEditPartner}>
            <EditOutlined/>
          </div>
          {(allowFullChanges || !representative) &&
            <div className={classNames(style.actionButtonContainer, 'cursor-pointer ml-4')}
              onClick={onDeletePartner}>
              <DeleteOutlined/>
            </div>}
        </div>}
      {(!isCompanyView && !!onlyRead && isAdmin && !hideVerifyIcon) && fucVerified}
    </div>;
  }, [isTCPProposal, representative, t, allowChangeRepresentative, canWrite, hasPermission, allowFullChanges,
    isCompanyView, onlyRead, onEditPartner, onDeletePartner, isAdmin, hideVerifyIcon, fucVerified, data,
    handleRepresentativeChange]);
  
  const isMarried = useMemo(() => civilStatus === CIVIL_STATUS.MARRIED, [civilStatus]);
  
  const onValidatePartnerRequest = useCallback(async () => {
    await verifyAsync({_id: proposalId, idNumber: idNumber});
  }, [idNumber, proposalId, verifyAsync]);
  
  const onRejectPartnerRequest = useCallback(async () => {
    await rejectAsync({_id: proposalId, idNumber: idNumber});
  }, [idNumber, proposalId, rejectAsync]);
  
  const wasProcessed = useMemo(() => status === PARTNERSHIP_REQUEST.ACCEPTED ||
    status === PARTNERSHIP_REQUEST.REJECTED ||
    representative, [representative, status]);
  
  return (
    <div>
      <div
        className={classNames('font-normal py-2 px-2 mb-4', {hidden: !isAdmin || !exist || isCompanyView})}
        style={{
          backgroundColor: '#FFE8D6',
          border: '#ECD0B8'
        }}
      >
        {t('proposalDetails.partnersAndWorkers.partners.partnerNotUnique')}
        <div>
          {proposals.map((entity, i) => <div key={i}>
            <Link to={`${ROUTE_PATH_PROPOSAL_REQUEST}/${entity._id}`}>
              <span className="mr-2">({entity?.code})</span>
              <ProposalSimilarItem value={entity}/>
            </Link>
          </div>)}
        </div>
      </div>
      <div>
        <div className="mb-3 flex items-center">
          <div className="flex flex-1 md:flex-none flex-col md:flex-row">
            <div className="text-lg font-bold">{getUserFullName(data)}</div>
            <div className="flex md:hidden">{actionCmp}</div>
          </div>
          <div className="hidden md:flex flex-1">{actionCmp}</div>
          {!!extraTitle && <div className="ml-2 flex-1">{extraTitle}</div>}
        </div>
        {(!isCompanyView && showStatusActions && !isTCPProposal) && <div>
          <VerifyListItem labelKey={t('proposalRequest.form.partners.status')} fullSpace>
            <div className="flex items-center justify-between w-full">
              <div className="flex-1 md:max-w-64"><PartnerStatus status={status}/></div>
              <div
                className={classNames(
                  'flex items-center',
                  {'hidden': !canWrite || wasProcessed || (user?.idNumber !== idNumber && !isAdmin)}
                )}>
                {status !== PARTNERSHIP_REQUEST.ACCEPTED &&
                  <Button key="validate" onClick={onValidatePartnerRequest} type={'primary'}
                    loading={rejecting || checking}>
                    {t('proposalDetails.partnersAndWorkers.partners.validateAdmin')}
                  </Button>}
                {status !== PARTNERSHIP_REQUEST.REJECTED &&
                  <Button key="rejected" onClick={onRejectPartnerRequest} type={'primary'}
                    loading={rejecting || checking}
                    className="bg-error text-white border-error ml-2">
                    {t('proposalDetails.partnersAndWorkers.partners.rejectAdmin')}
                  </Button>}
              </div>
            </div>
          </VerifyListItem>
        </div>}
        {!isTCPProposal && <>
          {(!isCNA && !disableContribution && !isTCPProposal) && <VerifyListItem
            labelKey={t('proposalRequest.form.partners.socialCapital.totalLabel')}
            fullSpace
            value={showPrice(totalAmount)}/>}
          {(isCompanyView || (onlyRead && !disableContribution && !cashEdit && !isTCPProposal)) ? <VerifyListItem
            labelKey={t('proposalRequest.form.partners.contribution')}>
            <div className="flex items-center gap-x-2">
              {showPrice(contribution || '0')}
              {isMarried && <span className="flex items-center">
                <span>(</span>
                <VerifyListItem labelKey={t('proposalRequest.form.partners.isMatrimonialCommunity.title')} removeSpace>
                  <BooleanPreview value={isMatrimonialCommunity}/>
                </VerifyListItem>
                <span>)</span>
              </span>}
            </div>
          </VerifyListItem> :
            ((!isStatePartnerForm && !disableContribution && !!cashEdit && !onlyRead && !isTCPProposal) &&
              <VerifyListItem
                labelKey={t(`proposalRequest.form.partners.${isProcedure ? 'procedureContribution' : 'contribution'}`)}
                fullSpace>
                <ContributionInput
                  {...props}
                  value={{value: contribution || 0, isMatrimonialCommunity}}
                  onChange={onChangeContribution}
                  isMarried={isMarried}/>
              </VerifyListItem>)}
        </>}
        {(!disableContribution && !isTCPProposal) &&
          <VerifyListItem labelKey={t('proposalRequest.form.partners.contributionBienes.label')} fullSpace>
            <ContributionGoods value={goods} onChange={onChangeGoods} index={index} onlyRead={onlyRead} partner={data}
              proposal={proposal}/>
          </VerifyListItem>}
        <VerifyListItem labelKey={t('address')} value={mainAddress}/>
        {bornPlace && <VerifyListItem labelKey={t('bornPlace')} fullSpace>
          <CountryPreview value={bornPlace} defaultValue={'-'}/>
        </VerifyListItem>}
        {workPlace && <VerifyListItem labelKey={t('proposalRequest.form.partners.modal.workPlace')} value={workPlace}/>}
        <VerifyListItem labelKey={t('email')} value={email || '-'}/>
        {gender && <VerifyListItem labelKey={t('gender')}>
          <GenderPreview value={gender} defaultValue={'-'}/>
        </VerifyListItem>}
        <VerifyListItem labelKey={t('skinColors.label')}>
          <SkinColorPreview value={skinColor} defaultValue={'-'}/>
        </VerifyListItem>
        <VerifyListItem labelKey={t('studyLevels.label')}>
          <StudyLevelPreview value={studyLevel} defaultValue={'-'}/>
        </VerifyListItem>
        <VerifyListItem labelKey={t('studyClassifications.label')}>
          <StudyClassificationPreview value={studyClassification} defaultValue={'-'}/>
        </VerifyListItem>
        <VerifyListItem labelKey={t('phones')}>
          <Phones value={phone} useLink={false}/>
        </VerifyListItem>
        
        <div className={classNames('flex md:items-center md:flex-row flex-col gap-x-4 mb-2')}>
          <div className="max-w-2/4">
            <VerifyListItem labelKey={t('dni.placeholder')} value={idNumber}/>
            <div className="flex items-center gap-x-2 mb-2 md:mb-0">
              {(dniPictures || []).map((e, index) => <div key={index} className="max-w-2/4">
                <Image className={'mr-5 logo w-full md:max-h-24 rounded border border-solid border-lightPrimary'}
                  src={getFromSource(e?.image || e?.thumb || e)}
                  alt={`${t('dni.placeholder')}-${index}`}/>
              </div>)}
            </div>
          </div>
        </div>
        
        {(civilStatus && !isStatePartnerForm && !isTCPProposal) && <div>
          <VerifyListItem labelKey={t('civilStatus.placeholder')} value={t(`civilStatus.${civilStatus}`)}/>
          {isMarried && <MarriedInformationDetails value={civilStatusInfo}/>}
        </div>}
        
        {!isStatePartnerForm && <>
          {onlyRead || !cashEdit ? <VerifyListItem labelKey={t('proposalRequest.form.partners.importBase.title')}>
            {!data?.isRetired ? <div className="flex flex-col md:flex-row md:items-center md:gap-x-2">
              <div className="flex items-center">
                {importBase < 0 ?
                  t('proposalRequest.form.partners.importBase.other') :
                  <>
                    {showPrice(importBase, '$', '0,0')}
                    <div className="mx-2 flex-1">({showPrice(importBase * 0.2, '$', '0,0')})</div>
                    <InfoBox title={t('proposalRequest.form.partners.importBase.helpPercent')}/>
                  </>
                }
              </div>
            </div> : t('proposalRequest.form.partners.isRetired.title')}
          </VerifyListItem> :
            <VerifyListItem
              labelKey={t('proposalRequest.form.partners.importBase.label')}
              fullSpace
              fullView
              className={'mb-4'}
              column>
              <div className="my-2">
                <Checkbox
                  checked={data?.isRetired}
                  className={classNames('mx-0 my-px')}
                  size={'large'}
                  onChange={(e) => onChangeIsRetired(e?.target?.checked)}>
                  {t('proposalRequest.form.partners.isRetired.label')}
                </Checkbox>
              </div>
              <div className="flex items-center gap-x-4">
                <div className="md:min-w-50">
                  <ProposalPartnerImportSelect value={importBase} onChange={onChangeImportBase}
                    disabled={data?.isRetired}/>
                </div>
                <InfoBox title={t('proposalRequest.form.partners.importBase.help')} withMargins={false}/>
              </div>
            </VerifyListItem>}
          {!isLast && <Divider className="mt-2"/>}
        </>}
      </div>
    </div>
  );
};

PartnerProposal.propTypes = {
  data: PropTypes.object,
  isLast: PropTypes.bool,
  isCNA: PropTypes.bool,
  isCompanyView: PropTypes.bool,
  allowChangeRepresentative: PropTypes.bool,
  showStatusActions: PropTypes.bool,
  onlyRead: PropTypes.bool,
  disableContribution: PropTypes.bool,
  isStatePartnerForm: PropTypes.bool,
  extraTitle: PropTypes.any,
  proposal: PropTypes.object,
  proposalId: PropTypes.string,
  companyId: PropTypes.string,
  index: PropTypes.number,
  onEditPartner: PropTypes.func,
  onDeletePartner: PropTypes.func,
  onChange: PropTypes.func,
  cashEdit: PropTypes.bool,
  hideStatus: PropTypes.bool,
  isTCPProposal: PropTypes.bool,
  allowFullChanges: PropTypes.bool,
  isProcedure: PropTypes.bool,
  hideVerifyIcon: PropTypes.bool
};

PartnerProposal.defaultProps = {
  isCNA: false
};

export default memo(PartnerProposal);
